import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, ChevronLeft, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { useAuditLogStore, type AuditLogEntry } from './useAuditLogStore';
import { useApiToken } from '@/auth/hooks';

// ─── Constants ──────────────────────────────────────────────────────────────────

const AUDIT_ACTIONS = [
  'UserLogin', 'UserLogout',
  'WorkspaceCreated', 'WorkspaceDeleted', 'WorkspaceRenamed',
  'WorkspaceMemberAdded', 'WorkspaceMemberRemoved', 'WorkspaceMemberRoleChanged',
  'PluginInstalled', 'PluginUninstalled', 'PluginSettingsChanged',
  'OrgMemberAdded', 'OrgMemberRemoved', 'OrgMemberRoleChanged',
  'DomainCreated', 'DomainDeleted',
  'DocumentUploaded', 'DocumentDeleted',
  'ConnectorCreated', 'ConnectorDeleted', 'ConnectorSyncTriggered',
] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function actionBadgeVariant(action: string): 'default' | 'destructive' | 'secondary' | 'outline' {
  if (action.includes('Deleted') || action.includes('Removed')) return 'destructive';
  if (action.includes('Created') || action.includes('Added')) return 'default';
  return 'secondary';
}

function exportCsv(entries: AuditLogEntry[]): void {
  const header = 'Zeitpunkt;Aktion;Benutzer;Workspace;Entity;Details;IP\n';
  const rows = entries.map(e =>
    [
      formatDateTime(e.createdAt),
      e.action,
      e.userDisplayName ?? e.userId,
      e.workspaceId ?? '',
      e.entityId ?? '',
      (e.details ?? '').replace(/;/g, ','),
      e.ipAddress ?? '',
    ].join(';'),
  ).join('\n');

  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function AuditLogPage({ embedded = false }: { embedded?: boolean }) {
  const { getAccessToken } = useApiToken();
  const entries = useAuditLogStore((s) => s.entries);
  const totalCount = useAuditLogStore((s) => s.totalCount);
  const page = useAuditLogStore((s) => s.page);
  const totalPages = useAuditLogStore((s) => s.totalPages);
  const filters = useAuditLogStore((s) => s.filters);
  const loading = useAuditLogStore((s) => s.loading);
  const error = useAuditLogStore((s) => s.error);
  const setFilters = useAuditLogStore((s) => s.setFilters);
  const setPage = useAuditLogStore((s) => s.setPage);
  const fetchLogs = useAuditLogStore((s) => s.fetch);

  const pageRef = useRef(page);
  pageRef.current = page;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Fetch on mount
  useEffect(() => {
    void fetchLogs(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch when page or filters change (skip initial)
  const initialRef = useRef(true);
  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }
    void fetchLogs(getAccessToken);
  }, [page, filters, fetchLogs, getAccessToken]);

  return (
    <div className={embedded ? '' : 'p-4 md:p-6'}>
      <div className="mx-auto w-full max-w-7xl space-y-4">
        {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Audit-Log</h1>
            <p className="text-sm text-muted-foreground">
              Protokollierte Aktionen in der Organisation.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCsv(entries)}
            disabled={entries.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            CSV Export
          </Button>
        </div>
        )}
        {embedded && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportCsv(entries)}
              disabled={entries.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              CSV Export
            </Button>
          </div>
        )}

        {/* Filter Bar */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-3 pt-4 pb-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Aktion</label>
              <select
                className="flex h-9 w-[200px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={filters.action ?? ''}
                onChange={(e) =>
                  setFilters({ ...filters, action: e.target.value || undefined })
                }
              >
                <option value="">Alle Aktionen</option>
                {AUDIT_ACTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Von</label>
              <Input
                type="date"
                className="w-[160px]"
                value={filters.from ?? ''}
                onChange={(e) =>
                  setFilters({ ...filters, from: e.target.value || undefined })
                }
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Bis</label>
              <Input
                type="date"
                className="w-[160px]"
                value={filters.to ?? ''}
                onChange={(e) =>
                  setFilters({ ...filters, to: e.target.value || undefined })
                }
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({})}
            >
              Zurücksetzen
            </Button>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {totalCount.toLocaleString('de-DE')} Einträge
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 py-8 text-destructive justify-center">
                <AlertTriangle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}
            {!loading && !error && (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[170px]">Zeitpunkt</TableHead>
                      <TableHead className="w-[200px]">Aktion</TableHead>
                      <TableHead>Benutzer</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Keine Einträge gefunden.
                        </TableCell>
                      </TableRow>
                    )}
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-xs tabular-nums">
                          {formatDateTime(entry.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={actionBadgeVariant(entry.action)}>
                            {entry.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {entry.userDisplayName ?? entry.userId}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entry.entityId ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">
                          {entry.details ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <span className="text-sm text-muted-foreground">
                      Seite {page} von {totalPages}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
